// The incremental half of the tiered Oracle snapshot load (SLICES.md § S7
// items 1 + 2, engine parity from item 8). `oracleSchemaCache.ts` owns the
// cache entry, the TTL and the phase orchestration; this module owns the DATA
// STRUCTURE those phases fill in.
//
// Mirrors `mssqlSnapshotMerge.ts` on top of `liveSnapshotMerge.ts`. Oracle's
// data dictionary is the largest of the five, so the same rule applies harder
// here: a page mutates only the relations it touched and re-projects only
// those, and the bare-name lookup indexes are maintained incrementally rather
// than rebuilt per page.

import type {
  FkEdge,
  RoutineMeta,
  SchemaSnapshot,
  TableMeta,
  UserTypeMeta,
} from '@/core/engines/intellisenseTypes';
import type { RoutineMetadata } from '@/types/electron';
import {
  projectOracleForeignKey,
  projectOracleObjectEntry,
} from '@/features/intellisense/schemaBus/adapters/oracleAdapter';
import { projectRoutines } from '@/features/intellisense/schemaBus/adapters/projectionHelpers';

import { forEachChunkYielding } from './introspectionPaging';
import {
  applyGlobalPage,
  applyPerRelationPage,
  claimGlobalReplace,
  createLiveSnapshot,
  type LiveSnapshot,
  reconcilePerRelationArtefact,
} from './liveSnapshotMerge';
import type { OracleRelationEntry, OracleRelationScoped } from './oracleSchemaIntrospection';
import type {
  OraclePropertyGraph,
  OracleSchemaColumn,
  OracleSchemaForeignKey,
  OracleSchemaObject,
  OracleSchemaPackage,
  OracleSchemaRoutine,
  OracleSchemaSequence,
  OracleSchemaSnapshot,
  OracleSchemaSynonym,
  OracleTypeAttribute,
} from './oracleSchemaTypes';

export type OracleLiveSnapshot = LiveSnapshot<OracleSchemaSnapshot, OracleSchemaObject> & {
  /** Mutable arrays shared by identity with `native` (and, where the bus
   *  shape matches, with `projected`). */
  readonly foreignKeys: OracleSchemaForeignKey[];
  readonly sequences: OracleSchemaSequence[];
  readonly synonyms: OracleSchemaSynonym[];
  readonly routines: OracleSchemaRoutine[];
  readonly packages: OracleSchemaPackage[];
  readonly userTypes: string[];
  /** Bus-shaped mirrors that need a mapping step rather than shared identity. */
  readonly projectedForeignKeys: FkEdge[];
  readonly projectedRoutines: RoutineMeta[];
  readonly projectedUserTypes: UserTypeMeta[];
  readonly projectedPropertyGraphs: OraclePropertyGraph[];
  readonly objectTypeAttributes: Record<string, { name: string; dataType: string }[]>;
};

/** Oracle folds unquoted identifiers to uppercase; the cache's own lookups
 *  have always been lower-cased, so the merge key is too. */
export function oracleTableKey(name: string): string {
  return name.toLowerCase();
}

/**
 * Carry the previous refresh's columns and CHECK clauses onto the freshly
 * fetched relation list — stale-while-revalidate, so a forced refresh does not
 * blank every relation's columns for as long as phase 2 takes.
 */
export function carryOverOracleIntrospection(
  previous: readonly OracleSchemaObject[] | undefined,
  fresh: readonly OracleRelationEntry[],
): OracleSchemaObject[] {
  const byKey = new Map<string, OracleSchemaObject>();
  for (const entry of previous ?? []) byKey.set(oracleTableKey(entry.name), entry);
  return fresh.map((entry) => {
    const prior = byKey.get(oracleTableKey(entry.name));
    const base: OracleSchemaObject = {
      name: entry.name,
      schema: entry.owner,
      type: entry.type,
      columns: prior ? prior.columns.slice() : [],
    };
    if (prior?.checkConstraints && prior.checkConstraints.length > 0) {
      base.checkConstraints = prior.checkConstraints.slice();
    }
    return base;
  });
}

/** Register one relation (and its current columns) in the lookup indexes. */
function indexRelation(live: OracleLiveSnapshot, obj: OracleSchemaObject): void {
  const native = live.native;
  const key = oracleTableKey(obj.name);
  native.objectsByNameLower[key] = obj;
  native.columnsByObjectNameLower[key] = obj.columns;
  for (const column of obj.columns) {
    (native.columnIndexByNameLower[column.name.toLowerCase()] ??= []).push({
      objectName: obj.name,
      objectType: obj.type,
      column,
    });
  }
}

/** Drop one relation's contributions to the column-name index (O(bucket)). */
function unindexColumns(
  live: OracleLiveSnapshot,
  obj: OracleSchemaObject,
  columns: readonly OracleSchemaColumn[],
): void {
  const index = live.native.columnIndexByNameLower;
  for (const column of columns) {
    const bucketKey = column.name.toLowerCase();
    const bucket = index[bucketKey];
    if (!bucket) continue;
    const kept = bucket.filter((row) => row.objectName !== obj.name);
    if (kept.length === 0) delete index[bucketKey];
    else index[bucketKey] = kept;
  }
}

/**
 * Build the live snapshot for a freshly fetched relation list, projecting the
 * relations in event-loop-yielding chunks so even a 200k-relation dictionary
 * never occupies the main thread for a single long task.
 */
export async function createOracleLiveSnapshot(
  native: OracleSchemaSnapshot,
  options?: { readonly yieldBetweenChunks?: () => Promise<void> },
): Promise<OracleLiveSnapshot> {
  const database = native.schema;
  const entries = native.tables.concat(native.views);

  const foreignKeys = (native.foreignKeys ?? []).slice();
  const sequences = native.sequences.slice();
  const synonyms = native.synonyms.slice();
  const routines = native.routines.slice();
  const packages = native.packages.slice();
  const userTypes = (native.userTypes ?? []).slice();
  // Share the mutable arrays with the native snapshot so `getCachedOracleSchema`
  // callers see phase 2 fill in without a second copy.
  native.foreignKeys = foreignKeys;
  native.sequences = sequences;
  native.synonyms = synonyms;
  native.routines = routines;
  native.packages = packages;
  native.userTypes = userTypes;

  const projectedForeignKeys: FkEdge[] = foreignKeys.map(projectOracleForeignKey);
  const projectedRoutines: RoutineMeta[] = projectRoutines(native.routinesByNameLower, database);
  const projectedUserTypes: UserTypeMeta[] = userTypes.map((name) => ({ name, kind: 'object' }));
  const projectedPropertyGraphs: OraclePropertyGraph[] = (native.propertyGraphs ?? []).slice();
  native.propertyGraphs = projectedPropertyGraphs;
  const objectTypeAttributes: Record<string, { name: string; dataType: string }[]> = {};
  for (const [typeName, attrs] of Object.entries(native.typeAttributesByTypeName ?? {})) {
    objectTypeAttributes[typeName.toUpperCase()] = attrs.map((a) => ({
      name: a.name,
      dataType: a.dataType,
    }));
  }

  const base = await createLiveSnapshot<OracleSchemaSnapshot, OracleSchemaObject>({
    native,
    entries,
    keyOf: (entry) => oracleTableKey(entry.name),
    projectEntry: (entry) => projectOracleObjectEntry(entry, database),
    buildProjected: (tables: TableMeta[]): SchemaSnapshot => ({
      engineId: 'oracle',
      connectionId: native.connectionId,
      database,
      schemas: native.schemas ?? [],
      tables,
      routines: projectedRoutines,
      foreignKeys: projectedForeignKeys,
      serverVersion: native.serverVersion || undefined,
      // Unlike the one-shot `projectOracleSnapshot`, the live projection always
      // carries these: they are shared by identity so phase 2 can fill them in,
      // and an empty value reads the same as an absent one to every consumer.
      userTypes: projectedUserTypes,
      objectTypeAttributes,
      propertyGraphs: projectedPropertyGraphs,
      builtAt: native.builtAt,
    }),
    yieldBetweenChunks: options?.yieldBetweenChunks,
  });

  // The native `tables` / `views` arrays must hold the SAME entry objects the
  // live snapshot indexes, so a consumer reading the native cache sees phase
  // 2's merges.
  native.tables = base.entries.filter((e) => e.type === 'table');
  native.views = base.entries.filter((e) => e.type !== 'table');

  const live: OracleLiveSnapshot = {
    ...base,
    foreignKeys,
    sequences,
    synonyms,
    routines,
    packages,
    userTypes,
    projectedForeignKeys,
    projectedRoutines,
    projectedUserTypes,
    projectedPropertyGraphs,
    objectTypeAttributes,
  };

  // Seed the bare-name lookups over whatever the carry-over brought along
  // (nothing at all on a cold load), in yielding chunks. Phase 2 keeps them in
  // step incrementally from there — a full rebuild per page would be quadratic.
  native.objectsByNameLower = {};
  native.columnsByObjectNameLower = {};
  native.columnIndexByNameLower = {};
  await forEachChunkYielding(
    live.entries,
    (chunk) => {
      for (const obj of chunk) indexRelation(live, obj);
    },
    { yieldBetweenChunks: options?.yieldBetweenChunks },
  );

  return live;
}

/**
 * Merge one page of column rows, replacing carried-over columns relation by
 * relation and keeping the bare-name lookup indexes in step incrementally.
 */
export function applyOracleColumnPage(
  live: OracleLiveSnapshot,
  columns: readonly OracleRelationScoped<OracleSchemaColumn>[],
): void {
  applyPerRelationPage(live, 'columns', columns, {
    keyOf: (c) => oracleTableKey(c.table),
    append: (entry, list, replace) => {
      const projected = list.map((c) => c.item);
      if (replace) {
        unindexColumns(live, entry, entry.columns);
        // Never mutates the existing array: a carried-over `columns` may still
        // be shared with the previous snapshot object a caller is holding.
        entry.columns = projected;
      } else {
        entry.columns = [...entry.columns, ...projected];
      }
      const key = oracleTableKey(entry.name);
      live.native.objectsByNameLower[key] = entry;
      live.native.columnsByObjectNameLower[key] = entry.columns;
      for (const column of projected) {
        (live.native.columnIndexByNameLower[column.name.toLowerCase()] ??= []).push({
          objectName: entry.name,
          objectType: entry.type,
          column,
        });
      }
    },
  });
}

export function applyOracleCheckPage(
  live: OracleLiveSnapshot,
  checks: readonly OracleRelationScoped<string>[],
): void {
  applyPerRelationPage(live, 'checks', checks, {
    keyOf: (c) => oracleTableKey(c.table),
    append: (entry, list, replace) => {
      const clauses = list.map((c) => c.item);
      entry.checkConstraints = replace
        ? clauses
        : [...(entry.checkConstraints ?? []), ...clauses];
    },
  });
}

/**
 * After the CHECK-constraint artefact was read completely, relations the
 * refresh never touched have no CHECK clauses any more — drop the ones carried
 * over from the previous refresh (release review 1.5.0, F5).
 */
export function reconcileOracleChecks(live: OracleLiveSnapshot): void {
  reconcilePerRelationArtefact(live, 'checks', (entry) => {
    if (!entry.checkConstraints || entry.checkConstraints.length === 0) return false;
    delete entry.checkConstraints;
    return true;
  });
}

export function applyOracleForeignKeyPage(
  live: OracleLiveSnapshot,
  edges: readonly OracleSchemaForeignKey[],
): void {
  applyGlobalPage(live, 'foreignKeys', live.foreignKeys, edges);
  // The projected mirror follows the same replace-then-append contract, so it
  // is refilled from the native list rather than appended to independently.
  live.projectedForeignKeys.length = 0;
  live.projectedForeignKeys.push(...live.foreignKeys.map(projectOracleForeignKey));
}

export function applyOracleSequencePage(
  live: OracleLiveSnapshot,
  sequences: readonly OracleSchemaSequence[],
): void {
  applyGlobalPage(live, 'sequences', live.sequences, sequences);
}

export function applyOracleSynonymPage(
  live: OracleLiveSnapshot,
  synonyms: readonly OracleSchemaSynonym[],
): void {
  applyGlobalPage(live, 'synonyms', live.synonyms, synonyms);
}

export function applyOracleRoutinePage(
  live: OracleLiveSnapshot,
  routines: readonly OracleSchemaRoutine[],
): void {
  applyGlobalPage(live, 'routines', live.routines, routines);
}

export function applyOracleUserTypePage(
  live: OracleLiveSnapshot,
  types: readonly string[],
): void {
  applyGlobalPage(live, 'userTypes', live.userTypes, types);
  live.projectedUserTypes.length = 0;
  for (const name of live.userTypes) live.projectedUserTypes.push({ name, kind: 'object' });
}

/** OBJECT-type attributes, bucketed by uppercase type name. */
export function applyOracleTypeAttributePage(
  live: OracleLiveSnapshot,
  attributes: readonly { typeName: string; attribute: OracleTypeAttribute }[],
): void {
  if (claimGlobalReplace(live, 'typeAttributes')) {
    live.native.typeAttributesByTypeName = {};
    for (const key of Object.keys(live.objectTypeAttributes)) {
      delete live.objectTypeAttributes[key];
    }
  }
  const byType = (live.native.typeAttributesByTypeName ??= {}) as Record<
    string,
    OracleTypeAttribute[]
  >;
  for (const { typeName, attribute } of attributes) {
    (byType[typeName] ??= []).push(attribute);
    (live.objectTypeAttributes[typeName] ??= []).push({
      name: attribute.name,
      dataType: attribute.dataType,
    });
  }
}

/**
 * Routine SIGNATURES (`ALL_ARGUMENTS`), bucketed by lower-cased name. The
 * first page of a refresh drops the carried-over map so a dropped routine
 * disappears; later pages append.
 */
export function applyOracleRoutineParams(
  live: OracleLiveSnapshot,
  routinesByNameLower: Record<string, RoutineMetadata[]>,
): void {
  claimGlobalReplace(live, 'routineParams');
  live.native.routinesByNameLower = routinesByNameLower;
  live.projectedRoutines.length = 0;
  live.projectedRoutines.push(...projectRoutines(routinesByNameLower, live.native.schema));
}

/** Whole-list replacements for the probe-shaped artefacts. */
export function setOraclePackages(
  live: OracleLiveSnapshot,
  packages: readonly OracleSchemaPackage[],
): void {
  live.packages.length = 0;
  live.packages.push(...packages);
}

export function setOraclePropertyGraphs(
  live: OracleLiveSnapshot,
  graphs: readonly OraclePropertyGraph[],
): void {
  live.projectedPropertyGraphs.length = 0;
  live.projectedPropertyGraphs.push(...graphs);
}
